import React from 'react';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Checkbox } from '../../../../checkbox';
import { Grid } from '../../../../grid';
import { CheckboxGroupInputProps, CheckboxGroupItemValueConstraint, CheckboxGroupValue } from './model';

export const CheckboxGroupInput = React.forwardRef<HTMLDivElement, CheckboxGroupInputProps<CheckboxGroupItemValueConstraint>>(
  <K extends CheckboxGroupItemValueConstraint>(
    {
      items,
      value = {} as CheckboxGroupValue<K>,
      onChange,
      error = false, // eslint-disable-line @typescript-eslint/no-unused-vars
      disabled = false,
      multiple = true,
      containerProps,
      itemSx,
      checkboxComponent,
      callback,
      sorters,
    }: CheckboxGroupInputProps<K>,
    ref,
  ) => {
    const CheckboxComponent = checkboxComponent || Checkbox;
    const theme = useTheme();

    const handleChange = (itemId: K) => (newItemValue: boolean) => {
      if (!onChange) {
        return;
      }

      if (multiple) {
        onChange({ ...value, [itemId]: newItemValue });
      } else {
        if (sorters) {
          onChange(itemId);
          // eslint-disable-next-line callback-return
          callback?.();
        } else {
          onChange({ [itemId]: newItemValue } as CheckboxGroupValue<K>);
        }
      }
    };

    return (
      <Grid container ref={ref} rowSpacing={2} spacing={1} {...containerProps}>
        {items.map(({ value: id, label, icon, disabled: itemDisabled }) => (
          <Grid key={id} sx={itemSx}>
            <CheckboxComponent
              disabled={disabled || itemDisabled}
              iconLeft={icon}
              label={label}
              name={String(id)}
              onChange={handleChange(id)}
              sx={{
                backgroundColor: sorters ? (id === value ? 'gray ultra light' : 'inherit') : 'inherit',
                justifyContent: sorters ? 'flex-start' : 'center',
                width: 1,
                border: sorters ? 'none' : 'inherit',
              }}
              value={value[id]}
            />
          </Grid>
        ))}
      </Grid>
    );
  },
) as <K extends CheckboxGroupItemValueConstraint>(
  props: CheckboxGroupInputProps<K> & React.RefAttributes<HTMLDivElement>,
) => ReturnType<React.FC>;
